import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _lowerCase from 'lodash/lowerCase';
import { useAppDispatch } from '../../redux/hooks';
import { fetchUserWorldCheckScreen } from '../../redux/modules/user/user.actions';
import { userSelector } from '../../redux/modules/user/user.selectors';
import { CopyToClipboardButton } from '../../components/CopyToClipboardButton';
import API_ERROR_CODES from '../../constants/apiErrorCodes';
import COLORS from '../../constants/colors';
import { CARD_BORDER, CodeBlock, Field, FieldGrid, SectionCard } from '../../components/Section';

interface WorldCheckTabProps {
  userId: number;
}

/** Values long enough to need their own block rather than sitting inline. */
const isLongValue = (value: unknown) => typeof value === 'string' && (value.length > 80 || value.includes('\n'));

export function WorldCheckTab({ userId }: WorldCheckTabProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('apiError');
  const [selectedTab, setSelectedTab] = React.useState<string>('0');
  const { userWorldCheckScreenData, error } = useSelector(userSelector);

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserWorldCheckScreen(Number(userId)));
    }
  }, []);

  const getWorldCheckStatus = (data: IAnyObject | null) => {
    if (!data) {
      return 'Not Found';
    }

    return data?.screenResult ? 'Success' : 'Failure';
  };

  const getStatusColor = (data: IAnyObject | null): 'error' | 'success' | 'warning' => {
    if (!data) {
      return 'warning';
    }

    return data?.screenResult ? 'success' : 'error';
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const renderAttributeValue = (
    key: string,
    value: string | number | boolean | object | object[],
    isFormat = false,
  ) => {
    if (Array.isArray(value)) {
      return value.map((item) => renderAttributeValue(key, item, isFormat)).join('\n');
    }

    if (key === 'matchedCriteria') {
      return Object.entries(value)
        .map(([k, v]) => `{ "${k}": ${renderAttributeValue(k, v, isFormat)} }`)
        .join('\n');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, isFormat ? null : undefined, isFormat ? 2 : 0);
    }

    if (typeof value === 'boolean') {
      return value ? 'TRUE' : 'FALSE';
    }

    return value;
  };

  const renderDataItems = () => {
    if (!userWorldCheckScreenData?.data.length) {
      return (
        <SectionCard title="Screening cases" sx={{ mt: '16px' }}>
          <Typography sx={{ fontSize: '0.875rem', color: COLORS.LIGHT_GRAY }}>
            No screening cases returned for this user.
          </Typography>
        </SectionCard>
      );
    }

    return (
      <SectionCard
        title="Screening cases"
        description={`${userWorldCheckScreenData.data.length} case(s) returned`}
        disablePadding
        sx={{ mt: '16px' }}
      >
        <TabContext value={selectedTab}>
          <Box sx={{ px: '8px', borderBottom: `1px solid ${CARD_BORDER}` }}>
            <TabList
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              aria-label="world-check-data-item"
            >
              {userWorldCheckScreenData.data.map((item, index) => (
                <Tab
                  key={`world-check-data-item-${item.id}`}
                  value={index.toString()}
                  label={`Case #${item.id}`}
                  sx={{ textTransform: 'none', fontWeight: 600, minHeight: '44px' }}
                  disableRipple
                />
              ))}
            </TabList>
          </Box>
          {userWorldCheckScreenData.data.map((item, index) => (
            <TabPanel key={`world-check-data-item-panel-${item.id}`} value={index.toString()} sx={{ p: '20px' }}>
              <FieldGrid>
                <Field label="Case id">
                  <CopyToClipboardButton value={item.id} />
                </Field>
                <Field label="Type" value={item.type} />
                {Object.entries(item.attributes as Record<string, any>).map(([key, value]) => {
                  const rendered = renderAttributeValue(key, value);

                  return (
                    <Field
                      key={`world-check-attribute-${key}`}
                      label={_lowerCase(key)}
                      fullWidth={isLongValue(rendered)}
                    >
                      {isLongValue(rendered) ? <CodeBlock value={rendered} maxHeight={220} /> : rendered}
                    </Field>
                  );
                })}
              </FieldGrid>
            </TabPanel>
          ))}
        </TabContext>
      </SectionCard>
    );
  };

  return (
    <>
      {error?.message && (
        <Alert
          // A user who has never been screened is informational, not a failure.
          severity={error.message === API_ERROR_CODES.SCREEN_NOT_FOUND ? 'info' : 'error'}
          sx={{ mb: '16px', borderRadius: '8px' }}
        >
          {t(error.message)}
        </Alert>
      )}

      <SectionCard title="Screening result">
        <FieldGrid>
          <Field label="World check status">
            <Chip
              label={getWorldCheckStatus(userWorldCheckScreenData)}
              size="small"
              color={getStatusColor(userWorldCheckScreenData)}
              variant="outlined"
              sx={{ fontWeight: 600, borderRadius: '6px' }}
            />
          </Field>
        </FieldGrid>
      </SectionCard>

      <SectionCard title="User data" description="Payload sent for screening" sx={{ mt: '16px' }}>
        <CodeBlock
          value={userWorldCheckScreenData?.user ? JSON.stringify(userWorldCheckScreenData.user, null, 2) : '—'}
        />
      </SectionCard>

      {renderDataItems()}
    </>
  );
}
